import type { EventSurface, SuggestionArm } from "@/domain/suggestions";

/**
 * Bridges "this item detail was opened from a suggestion card" to
 * `BottomButtons`'s add-to-cart handler, across a route navigation — the two
 * live in unrelated component trees (a suggestion card in the cart/home/item
 * page vs. the item modal), so this is a small event-bus, same shape as
 * `fly-to-cart.ts`'s.
 *
 * A suggestion whose offer HAS required option groups cannot be added with
 * one tap: the card navigates to item detail instead, and the ADD event (with
 * `addedVia`, arm and anchor) must still fire once the customer finishes the
 * options and taps "Adicionar" there.
 */
export type PendingSuggestionNav = { itemOfferId: string; surface: EventSurface; arm: SuggestionArm; anchorItemId?: string };

let pending: PendingSuggestionNav | null = null;

/** Call right before navigating to an item's detail from a suggestion card. */
export function markSuggestionNavigation(
  itemOfferId: string,
  surface: EventSurface,
  arm: SuggestionArm,
  anchorItemId?: string,
): void {
  pending = { itemOfferId, surface, arm, ...(anchorItemId ? { anchorItemId } : {}) };
}

/**
 * Consumes (clears) the pending marker if it matches `itemOfferId`. Returns
 * the surface/arm/anchor to tag the resulting cart line/event with, or `null`
 * when this item detail was opened through the regular menu.
 */
export function consumeSuggestionNavigation(itemOfferId: string): Omit<PendingSuggestionNav, "itemOfferId"> | null {
  if (!pending || pending.itemOfferId !== itemOfferId) return null;
  const { surface, arm, anchorItemId } = pending;
  pending = null;
  return { surface, arm, ...(anchorItemId ? { anchorItemId } : {}) };
}

/** Tests only. */
export function resetSuggestionNavigation(): void {
  pending = null;
}
